import { type Phase, phaseDefinitions } from '@lib/trip';
import type { ApiKitTrip, Pcl } from '@tuic/api-kit';
import { Token } from '@lib/token';
import { GetSlashCredentials } from '@lib/third-parties/get-slash.types';

export type PhaserAttributes = {
  datetime?: string;
  mockInternetCard?: boolean;
  vip?: number;
  tariff?: string;
  mpi?: number;
  cabinNumber?: string;
  shipCode?: string;
  bus?: boolean;
  trainVariant?: number;
  flightVariant?: number;
  isDebugMode?: boolean;
  isEmptyStateForced?: boolean;
};

export type WithPhaserParams<P extends {} = Record<string, string>> = P & {
  'phaser[datetime]'?: string;
  'phaser[mockInternetCard]'?: string;
  'phaser[vip]'?: string;
  'phaser[tariff]'?: string;
  'phaser[mpi]'?: string;
  'phaser[cabinNumber]'?: string;
  'phaser[shipCode]'?: string;
  'phaser[bus]'?: string;
  'phaser[isDebugMode]'?: string;
  'phaser[isEmptyStateForced]'?: string;
};

export type PhaserClientData = {
  token: Token;
  serializedPhasedBooking: string;
  trips: ApiKitTrip[];
  stages: Record<string, Pcl.Stage[]>;
  phases: typeof phaseDefinitions;
  currentTrip: ApiKitTrip;
  currentStageIdx: number;
  currentPhase: Phase;
  currentDateTime: string;
  currentVip?: number;
  currentTariff?: string;
  dismissedAlertIds: string[];
  contentful: {
    space: string;
    environment: string;
  };
  getSlash: {
    credentials: GetSlashCredentials | undefined;
  };
};

// Types for Transport / Transit
export type TrainVariant = 2 | 3 | 4 | 5 | 6;
export type FlightVariant = 0 | 1 | 2 | 11 | 22;

export type TransportState = {
  bus?: boolean;
  trainVariant?: TrainVariant;
  flightVariant?: FlightVariant;
  isDebugMode?: boolean;
  isEmptyStateForced?: boolean;
};

export type TransportResult = {
  state: string;
  text?: string;
};

/**
 * Mapping of transport combinations to their resulting states and display texts.
 * Key format: '{flight}-{bus}-{trainVariant}'
 * - flight: boolean indicating if flight is selected
 * - bus: boolean indicating if bus is selected
 * - trainVariant: number (2-6) representing train booking status
 *   - 5: Empty/No train
 *   - 6: Not bookable
 *   - 2: Bookable
 *   - 3: Needs check
 *   - 4: Needs rebooking
 *
 * States:
 * - empty: No transport selected
 * - hasFlights: Only flight selected
 * - hasBus: Only bus selected
 * - trainNotBookable: Train variant 6 with optional flight
 * - trainBookable: Train variant 2 with optional flight
 * - trainNeedsCheck: Train variant 3 with optional flight
 * - trainNeedsRebook: Train variant 4 with optional flight
 *
 * Text values indicate the type of journey:
 * - 'Rail & Cruise': Train-only journey
 * - 'Rail & Fly': Combined train and flight journey
 */
export const transportStates: Record<string, TransportResult> = {
  'false-false-5': { state: 'empty' },
  'false-false-6': { state: 'trainNotBookable', text: 'Rail & Cruise' },
  'false-false-2': { state: 'trainBookable', text: 'Rail & Cruise' },
  'false-false-3': { state: 'trainNeedsCheck', text: 'Rail & Cruise' },
  'false-false-4': { state: 'trainNeedsRebook', text: 'Rail & Cruise' },
  'true-false-5': { state: 'hasFlights' },
  'true-false-6': { state: 'trainNotBookable', text: 'Rail & Fly' },
  'true-false-2': { state: 'trainBookable', text: 'Rail & Fly' },
  'true-false-3': { state: 'trainNeedsCheck', text: 'Rail & Fly' },
  'true-false-4': { state: 'trainNeedsRebook', text: 'Rail & Fly' },
  'false-true-5': { state: 'hasBus' },
};
